import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import {
  MdEventAvailable, MdPayments, MdPersonAdd, MdAssessment, MdCampaign, MdGppMaybe, MdLogin,
  MdVolumeUp, MdVolumeOff, MdGroups, MdMoneyOff, MdWifiTethering, MdChevronLeft, MdAccessTime,
  MdCastForEducation, MdSchool, MdFamilyRestroom, MdBusinessCenter,
} from 'react-icons/md';
import api from '../../ui/api';
import useSounds from '../../hooks/useSounds';
import { useNavigate } from '../navIntent';
import { useAuth } from '../auth';
import './monitor.css';

/**
 * لوحة المراقبة — the school, right now.
 *
 * Numbers (api /monitor/summary, every 20s) and a live feed of what just
 * happened (api /monitor/feed, every 6s). A new event slides into the
 * feed, pops a toast and — unless muted — plays the notification sound,
 * so the office sees a register being taken or a payment landing without
 * touching the screen. Clicking an event opens the pupil or class it is
 * about in شؤون الطلاب.
 */

const FEED_MS = 6000;
const SUMMARY_MS = 20000;
const SOUND_KEY = 'mtz.monitor.sound';

const KIND = {
  attendance: { icon: MdEventAvailable, label: 'تفقّد', tone: 'info', toast: true },
  payment: { icon: MdPayments, label: 'أقساط', tone: 'success', toast: true },
  student: { icon: MdPersonAdd, label: 'طلاب', tone: 'primary', toast: true },
  exam: { icon: MdAssessment, label: 'علامات', tone: 'violet', toast: true },
  notification: { icon: MdCampaign, label: 'إشعارات', tone: 'warning', toast: true },
  security: { icon: MdGppMaybe, label: 'أمان', tone: 'danger', toast: true },
  login: { icon: MdLogin, label: 'دخول', tone: 'neutral', toast: false },
};

const FILTERS = [
  { id: 'all', label: 'الكل' },
  { id: 'attendance', label: 'التفقّد' },
  { id: 'payment', label: 'الأقساط' },
  { id: 'student', label: 'الطلاب' },
  { id: 'exam', label: 'العلامات' },
  { id: 'login', label: 'الدخول' },
  { id: 'security', label: 'الأمان' },
];

const money = (v) => `$${Number(v || 0).toLocaleString('en-US')}`;
const pct = (a, b) => (b > 0 ? Math.round((a / b) * 100) : 0);

function ago(iso, now) {
  const s = Math.max(0, Math.round((now - Date.parse(iso)) / 1000));
  if (s < 45) return 'الآن';
  const m = Math.round(s / 60);
  if (m < 60) return `قبل ${m} د`;
  const h = Math.round(m / 60);
  if (h < 24) return `قبل ${h} س`;
  return `قبل ${Math.round(h / 24)} يوم`;
}

const clock = (iso) => new Date(iso).toLocaleTimeString('ar-SY-u-nu-latn', { hour: '2-digit', minute: '2-digit' });

/** A number that counts to its new value instead of jumping. */
function CountUp({ value, format = (v) => v.toLocaleString('en-US') }) {
  const [shown, setShown] = useState(value || 0);
  const from = useRef(value || 0);
  useEffect(() => {
    const start = from.current;
    const end = value || 0;
    if (start === end) return undefined;
    const t0 = performance.now();
    let raf;
    const step = (t) => {
      const k = Math.min(1, (t - t0) / 700);
      const eased = 1 - (1 - k) ** 3;
      setShown(Math.round(start + (end - start) * eased));
      if (k < 1) raf = requestAnimationFrame(step);
      else from.current = end;
    };
    raf = requestAnimationFrame(step);
    return () => { cancelAnimationFrame(raf); from.current = end; };
  }, [value]);
  return <>{format(shown)}</>;
}

/** Donut gauge, 0–100. */
function Ring({ value, size = 92, stroke = 10, tone = 'success', children }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  return (
    <div className={`mon-ring mon-ring-${tone}`} style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size / 2} cy={size / 2} r={r} className="mon-ring-track" strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          className="mon-ring-bar"
          strokeWidth={stroke}
          strokeDasharray={c}
          strokeDashoffset={c * (1 - Math.min(100, Math.max(0, value)) / 100)}
        />
      </svg>
      <div className="mon-ring-center">{children}</div>
    </div>
  );
}

function LiveClock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  return (
    <div className="mon-clock">
      <span className="mon-clock-time">
        {now.toLocaleTimeString('ar-SY-u-nu-latn', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
      </span>
      <span className="mon-clock-date">
        {now.toLocaleDateString('ar-SY-u-nu-latn', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
      </span>
    </div>
  );
}

export default function Monitor() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const sounds = useRef(null);
  sounds.current = useSounds();

  const [summary, setSummary] = useState(null);
  const [summaryAt, setSummaryAt] = useState(null);
  const [events, setEvents] = useState([]);
  const [feedReady, setFeedReady] = useState(false);
  const [filter, setFilter] = useState('all');
  const [now, setNow] = useState(Date.now());
  const [sound, setSound] = useState(() => {
    try { return localStorage.getItem(SOUND_KEY) !== '0'; } catch { return true; }
  });
  const soundRef = useRef(sound);
  soundRef.current = sound;

  const since = useRef(null);
  const seen = useRef(new Set());
  // The first load fills the feed quietly; only later arrivals announce.
  const loadedOnce = useRef(false);
  const busy = useRef(false);

  const loadSummary = useCallback(() => api.monitorSummary()
    .then((s) => { setSummary(s); setSummaryAt(Date.now()); })
    .catch(() => { /* keep the last numbers; the navbar says we are offline */ }), []);

  const loadFeed = useCallback(async () => {
    // A slow poll must not overlap the next one.
    if (busy.current) return;
    busy.current = true;
    let res;
    try {
      res = await api.monitorFeed(since.current ? { since: since.current, limit: 60 } : { limit: 40 });
    } catch { return; } finally { busy.current = false; }
    // Re-ask a few seconds back next time (rows written during this
    // request are not lost); `seen` drops the overlap.
    since.current = new Date(Date.parse(res.now) - 5000).toISOString();
    const fresh = res.events.filter((e) => !seen.current.has(e.key));
    fresh.forEach((e) => seen.current.add(e.key));
    const first = !loadedOnce.current;
    loadedOnce.current = true;
    if (!fresh.length) { setFeedReady(true); return; }

    const stamped = fresh.map((e) => ({ ...e, isNew: !first }));
    setEvents((cur) => [...stamped, ...cur.map((e) => ({ ...e, isNew: false }))].slice(0, 150));
    setFeedReady(true);
    if (first) return;

    // Something happened: tell the room, and refresh the numbers now.
    loadSummary();
    const loud = fresh.filter((e) => KIND[e.kind]?.toast);
    if (loud.length && soundRef.current) sounds.current.notification();
    loud.slice(0, 3).forEach((e) => {
      const Icon = KIND[e.kind]?.icon || MdCampaign;
      toast(
        <div className="mon-toast">
          <span className={`mon-ico mon-tone-${KIND[e.kind]?.tone}`}><Icon size={16} /></span>
          <span><strong>{e.title}</strong><small>{e.detail}</small></span>
        </div>,
        { autoClose: 5000, closeButton: false }
      );
    });
    if (loud.length > 3) toast.info(`و${loud.length - 3} أحداث أخرى`);
  }, [loadSummary]);

  // Refs so the intervals below are set once, not rebuilt every render.
  const feedRef = useRef(loadFeed);
  feedRef.current = loadFeed;

  useEffect(() => {
    loadSummary();
    feedRef.current();
    const a = setInterval(() => feedRef.current(), FEED_MS);
    const b = setInterval(loadSummary, SUMMARY_MS);
    const c = setInterval(() => setNow(Date.now()), 15000);
    return () => { clearInterval(a); clearInterval(b); clearInterval(c); };
  }, [loadSummary]);

  const toggleSound = () => {
    const next = !sound;
    setSound(next);
    try { localStorage.setItem(SOUND_KEY, next ? '1' : '0'); } catch { /* ignore */ }
    if (next) sounds.current.notification();
  };

  const shown = useMemo(
    () => (filter === 'all' ? events : events.filter((e) => e.kind === filter)),
    [events, filter]
  );

  const openRef = (e) => {
    if (!e.ref) return;
    if (e.ref.type === 'student') navigate('student-directory', { studentId: e.ref.id });
    else if (e.ref.type === 'section') navigate('student-directory', { sectionId: e.ref.id });
  };

  const a = summary?.attendance;
  const m = isAdmin ? summary?.money : null;
  const p = summary?.portal;
  // A late pupil still came: the rate is everyone who is in school.
  const presentRate = a ? pct(a.present + a.late, a.students_counted) : 0;
  const takenRate = a ? pct(a.taken, a.sections) : 0;
  const billed = m ? m.students_paid_month + m.students_unpaid_month : 0;
  const paidRate = m ? pct(m.students_paid_month, billed) : 0;
  const monthCollectedRate = m ? pct(m.month_cents, m.month_cents + m.remaining_cents) : 0;

  return (
    <div className="mon">
      <header className="mon-hero">
        <div className="mon-hero-text">
          <span className="mon-live"><span className="mon-live-dot" /> مباشر</span>
          <h1>حالة المدرسة الآن</h1>
          <p>
            تتحدث تلقائياً — الأرقام كل {SUMMARY_MS / 1000} ثانية والأحداث كل {FEED_MS / 1000} ثوانٍ
            {summaryAt ? ` · آخر تحديث ${ago(new Date(summaryAt).toISOString(), now)}` : ''}
          </p>
        </div>
        <LiveClock />
        <button
          type="button"
          className={`mon-sound${sound ? ' is-on' : ''}`}
          onClick={toggleSound}
          title={sound ? 'كتم صوت التنبيهات' : 'تشغيل صوت التنبيهات'}
        >
          {sound ? <MdVolumeUp size={20} /> : <MdVolumeOff size={20} />}
          <span>{sound ? 'الصوت مفعّل' : 'الصوت مكتوم'}</span>
        </button>
      </header>

      <section className="mon-kpis">
        <div className="mon-kpi mon-kpi-att">
          <div className="mon-kpi-top"><MdGroups size={20} /> حضور اليوم</div>
          <div className="mon-kpi-num">{a ? <><CountUp value={presentRate} />%</> : '—'}</div>
          <div className="mon-kpi-sub">
            {a ? <>غياب <b><CountUp value={a.absent} /></b> · تأخير <b><CountUp value={a.late} /></b></> : 'جارٍ التحميل…'}
          </div>
        </div>
        {isAdmin && <><div className="mon-kpi mon-kpi-money">
          <div className="mon-kpi-top"><MdPayments size={20} /> المقبوض اليوم</div>
          <div className="mon-kpi-num">{m ? <CountUp value={m.today_cents} format={money} /> : '—'}</div>
          <div className="mon-kpi-sub">{m ? <><b>{m.today_count}</b> دفعة · الأسبوع {money(m.week_cents)}</> : ' '}</div>
        </div>
        <div className="mon-kpi mon-kpi-unpaid">
          <div className="mon-kpi-top"><MdMoneyOff size={20} /> لم يدفعوا هذا الشهر</div>
          <div className="mon-kpi-num">{m ? <CountUp value={m.students_unpaid_month} /> : '—'}</div>
          <div className="mon-kpi-sub">{m ? <>المتبقي <b>{money(m.remaining_cents)}</b></> : ' '}</div>
        </div></>}
        <div className="mon-kpi mon-kpi-online">
          <div className="mon-kpi-top"><MdWifiTethering size={20} /> متصلون الآن</div>
          <div className="mon-kpi-num">{p ? <CountUp value={p.online_total} /> : '—'}</div>
          <div className="mon-kpi-sub">{p ? <>دخل اليوم <b>{p.active_today}</b> مستخدم</> : ' '}</div>
        </div>
      </section>

      <div className="mon-grid">
        <section className="mon-card mon-feed">
          <div className="mon-card-head">
            <h2>ما يحدث الآن</h2>
            <span className="mon-count">{events.length}</span>
          </div>
          <div className="mon-filters">
            {FILTERS.filter((f) => isAdmin || f.id !== 'payment').map((f) => {
              const n = f.id === 'all' ? events.length : events.filter((e) => e.kind === f.id).length;
              return (
                <button
                  key={f.id}
                  type="button"
                  className={`mon-filter${filter === f.id ? ' is-active' : ''}`}
                  onClick={() => setFilter(f.id)}
                >
                  {f.label}{n ? <span>{n}</span> : null}
                </button>
              );
            })}
          </div>
          <ul className="mon-events">
            {!feedReady && [0, 1, 2, 3, 4].map((k) => <li key={k} className="mon-skel" />)}
            {feedReady && shown.length === 0 && (
              <li className="mon-empty">لا أحداث بعد اليوم. ستظهر هنا فور حدوثها.</li>
            )}
            {shown.map((e) => {
              const k = KIND[e.kind] || KIND.notification;
              const Icon = k.icon;
              const clickable = !!e.ref && (e.ref.type === 'student' || e.ref.type === 'section');
              return (
                <li
                  key={e.key}
                  className={`mon-event${e.isNew ? ' is-new' : ''}${clickable ? ' is-link' : ''}${k.toast ? '' : ' is-quiet'}`}
                  onClick={clickable ? () => openRef(e) : undefined}
                  title={clickable ? 'فتح في شؤون الطلاب' : undefined}
                >
                  <span className={`mon-ico mon-tone-${k.tone}`}><Icon size={17} /></span>
                  <span className="mon-event-main">
                    <strong>{e.title}</strong>
                    {e.detail && <small>{e.detail}</small>}
                  </span>
                  <span className="mon-event-time">
                    <span>{ago(e.at, now)}</span>
                    <small>{clock(e.at)}</small>
                  </span>
                  {clickable && <MdChevronLeft className="mon-event-go" size={18} />}
                </li>
              );
            })}
          </ul>
        </section>

        <aside className="mon-side">
          <section className="mon-card">
            <div className="mon-card-head"><h2>التفقّد اليوم</h2></div>
            {a ? (
              <>
                <div className="mon-att">
                  <Ring value={presentRate} tone="success">
                    <b>{presentRate}%</b><small>نسبة الحضور</small>
                  </Ring>
                  <ul className="mon-legend">
                    <li><i className="dot-success" /> حاضر <b>{a.present}</b></li>
                    <li><i className="dot-danger" /> غائب <b>{a.absent}</b></li>
                    <li><i className="dot-warning" /> متأخر <b>{a.late}</b></li>
                  </ul>
                </div>
                <div className="mon-bar-row">
                  <span>الشعب التي تفقّدت</span>
                  <b>{a.taken} / {a.sections}</b>
                </div>
                <div className="mon-bar"><span style={{ width: `${takenRate}%` }} /></div>
                {a.last ? (
                  <button
                    type="button"
                    className="mon-last"
                    onClick={() => navigate('student-directory', { sectionId: a.last.section_id })}
                  >
                    <MdAccessTime size={16} />
                    <span>
                      آخر تفقّد: <b>{a.last.section_label}</b>
                      <small>{a.last.marked_by_name} — {clock(a.last.updated_at)}</small>
                    </span>
                  </button>
                ) : (
                  <p className="mon-muted">لم يُؤخذ أي تفقّد اليوم بعد.</p>
                )}
              </>
            ) : <div className="mon-skel mon-skel-lg" />}
          </section>

          {isAdmin && <section className="mon-card">
            <div className="mon-card-head"><h2>الأقساط</h2></div>
            {m ? (
              <>
                <div className="mon-money">
                  {[
                    ['اليوم', m.today_cents, m.today_count],
                    ['هذا الأسبوع', m.week_cents, m.week_count],
                    ['هذا الشهر', m.month_cents, m.month_count],
                  ].map(([label, cents, n]) => (
                    <div key={label} className="mon-money-row">
                      <span>{label}</span>
                      <b><CountUp value={cents} format={money} /></b>
                      <small>{n} دفعة</small>
                    </div>
                  ))}
                </div>
                <div className="mon-bar-row">
                  <span>دفعوا قسط هذا الشهر</span>
                  <b>{m.students_paid_month} / {billed}</b>
                </div>
                <div className="mon-bar mon-bar-success"><span style={{ width: `${paidRate}%` }} /></div>
                <div className="mon-bar-row">
                  <span>المُحصّل من المستحق</span>
                  <b>{monthCollectedRate}%</b>
                </div>
                <div className="mon-bar mon-bar-info"><span style={{ width: `${monthCollectedRate}%` }} /></div>
                <p className="mon-muted">
                  {m.students_no_fee} طالب بلا رسوم أو معفى — لا يُحسبون ضمن غير الدافعين.
                </p>
              </>
            ) : <div className="mon-skel mon-skel-lg" />}
          </section>}

          <section className="mon-card">
            <div className="mon-card-head">
              <h2>البوابة الآن</h2>
              {p && <span className="mon-muted">آخر {p.online_minutes} دقائق</span>}
            </div>
            {p ? (
              <>
                <div className="mon-online">
                  {[
                    ['المعلمون', p.online.teachers, MdCastForEducation, 'success'],
                    ['الطلاب', p.online.students, MdSchool, 'primary'],
                    ['أولياء الأمور', p.online.parents, MdFamilyRestroom, 'violet'],
                    ['الإدارة', p.online.office, MdBusinessCenter, 'info'],
                  ].map(([label, n, Icon, tone]) => (
                    <div key={label} className={`mon-online-tile${n ? ' is-on' : ''}`}>
                      <span className={`mon-ico mon-tone-${tone}`}><Icon size={17} /></span>
                      <b><CountUp value={n} /></b>
                      <small>{label}</small>
                    </div>
                  ))}
                </div>
                {p.security_events_today > 0 && (
                  <button type="button" className="mon-alert" onClick={() => setFilter('security')}>
                    <MdGppMaybe size={18} /> {p.security_events_today} تنبيه أمني اليوم — عرض
                  </button>
                )}
                <BackupLine b={summary?.backup} />
              </>
            ) : <div className="mon-skel mon-skel-lg" />}
          </section>
        </aside>
      </div>
    </div>
  );
}

/** Last database backup, from the summary. Red when the last automatic
 *  run failed or nothing was saved in the last 24 hours. */
function BackupLine({ b }) {
  if (b === undefined) return null;
  const last = b?.last;
  const age = last ? Date.now() - new Date(last.created_at).getTime() : Infinity;
  const bad = !last || age > 24 * 3600 * 1000 || !!b?.last_error;
  const when = last
    ? new Date(last.created_at).toLocaleString('ar-SY', { dateStyle: 'medium', timeStyle: 'short' })
    : 'لا توجد نسخة بعد';
  return (
    <p className={`mon-backup${bad ? ' is-bad' : ''}`}>
      <span>آخر نسخة احتياطية</span>
      <b>{when}{last ? ` — ${last.kind_label}` : ''}</b>
      {b?.last_error && <small>فشلت آخر نسخة تلقائية</small>}
    </p>
  );
}
